'use client';

import { useEffect, useState } from 'react';

/**
 * Hook for calculating responsive container height based on viewport
 *
 * @param isActive Whether the container is active/visible
 * @param config Configuration options
 * @param config.maxHeightRatio Maximum height as a ratio of viewport height (default: 0.8)
 * @param config.minHeight Minimum height in pixels (default: 300)
 * @param config.offsetPx Pixel offset to account for headers/margins (default: 100)
 * @returns Calculated container height as a CSS value
 */
export function useResponsiveHeight(
  isActive: boolean,
  config: {
    maxHeightRatio?: number;
    minHeight?: number;
    offsetPx?: number;
  } = {},
) {
  const {
    maxHeightRatio = 0.8,
    minHeight = 300,
    offsetPx = 100,
  } = config;

  const [containerHeight, setContainerHeight] = useState('60vh');

  useEffect(() => {
    if (isActive === true) {
      const calculateHeight = () => {
        const viewportHeight = window.innerHeight;
        const maxHeight = Math.floor(viewportHeight * maxHeightRatio);
        const responsiveHeight = Math.max(minHeight, maxHeight - offsetPx);
        setContainerHeight(`${responsiveHeight}px`);
      };

      // Calculate initial height
      calculateHeight();

      // Update on resize
      window.addEventListener('resize', calculateHeight);
      return () => window.removeEventListener('resize', calculateHeight);
    }

    return undefined; // Explicit return for when isActive is false
  }, [isActive, maxHeightRatio, minHeight, offsetPx]);

  return containerHeight;
}

export default useResponsiveHeight;
